/**
 * Level4Scene - 关卡4：摩擦力探索
 * 教学目标：理解摩擦力公式 f=μN
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import type { LevelConfig, FrictionLevelsConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

// 摩擦系数常量
const FRICTION_ICE = 0.1;
const FRICTION_WOOD = 0.4;
const FRICTION_SANDPAPER = 0.8;

// 颜色常量
const COLOR_ICE = 0x87ceeb;
const COLOR_WOOD = 0x8b4513;
const COLOR_SANDPAPER = 0xbdb76b;
const COLOR_GOAL = 0x4ade80;

// 默认摩擦材质配置
const DEFAULT_FRICTION_LEVELS: FrictionLevelsConfig = {
  ice: { coefficient: FRICTION_ICE, name: '冰面', color: COLOR_ICE, description: '极低摩擦，滑动距离最长' },
  wood: { coefficient: FRICTION_WOOD, name: '木地板', color: COLOR_WOOD, description: '中等摩擦，滑动距离适中' },
  sandpaper: { coefficient: FRICTION_SANDPAPER, name: '砂纸', color: COLOR_SANDPAPER, description: '高摩擦，滑动距离最短' },
};

export interface Level4Config extends LevelConfig {
  frictionLevels?: FrictionLevelsConfig;
}

/**
 * Level4Scene - 摩擦力教学关卡场景
 */
export class Level4Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private levelConfig: Level4Config;
  private frictionLevels: FrictionLevelsConfig;
  private selectedMaterial: string = 'wood';
  private goalZone!: Phaser.Geom.Rectangle;
  private isVictory: boolean = false;
  private platformGraphics!: Phaser.GameObjects.Graphics;
  private materialButtons: Map<string, Phaser.GameObjects.Container> = new Map();
  private elapsedTime: number = 0;

  constructor(config?: Level4Config) {
    super({ key: 'Level4Scene' });
    this.levelConfig = config ?? this.getDefaultConfig();
    this.frictionLevels = this.levelConfig.frictionLevels ?? DEFAULT_FRICTION_LEVELS;
  }

  /**
   * 设置关卡配置
   */
  setConfig(config: Level4Config): void {
    this.levelConfig = config;
    this.frictionLevels = config.frictionLevels ?? DEFAULT_FRICTION_LEVELS;
  }

  preload(): void {
    // 无资源需要预加载
  }

  create(): void {
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(this.levelConfig.physics.gravity.x, this.levelConfig.physics.gravity.y),
      allowSleep: this.levelConfig.physics.allowSleep ?? true,
    });

    this.createBoundaries();
    this.createFrictionPlatforms();
    this.createStickman();
    this.createGoalZone();
    this.createMaterialButtons();
    this.createTutorialUI();
    this.createStatusUI();

    this.isVictory = false;
    this.elapsedTime = 0;
  }

  override update(_time: number, delta: number): void {
    if (this.isVictory) return;

    const dt = delta / 1000;
    this.elapsedTime += dt;
    this.physicsWorld.step(dt);
    this.stickman.update();
    this.checkVictory();
  }

  /**
   * 创建边界
   */
  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    // 绘制边界线
    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  /**
   * 创建三段不同摩擦系数的平台
   */
  private createFrictionPlatforms(): void {
    this.platformGraphics = this.add.graphics();
    
    const platforms = this.levelConfig.platforms;
    
    for (const platform of platforms) {
      const friction = platform.friction ?? FRICTION_WOOD;
      const color = platform.color ?? COLOR_WOOD;
      
      // 创建物理平台
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction,
        restitution: platform.restitution ?? 0,
      });

      // 绘制平台（将物理坐标转换为屏幕坐标）
      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      
      this.platformGraphics.fillStyle(color, 1);
      this.platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
      
      // 添加材质标签
      const materialName = this.getMaterialNameByFriction(friction);
      this.add.text(screenX + platform.size.width / 2, screenY - 15, materialName, {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  /**
   * 根据摩擦系数获取材质名称
   */
  private getMaterialNameByFriction(friction: number): string {
    if (friction <= 0.15) return '冰面 (μ=0.1)';
    if (friction <= 0.5) return '木地板 (μ=0.4)';
    return '砂纸 (μ=0.8)';
  }

  /**
   * 创建火柴人角色
   */
  private createStickman(): void {
    const playerConfig = this.levelConfig.player;
    const startY = playerConfig.startPosition.y;
    const startX = playerConfig.startPosition.x;

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: 8,
        jumpForce: 150,
      },
      GAME_HEIGHT
    );
  }

  /**
   * 创建目标区域
   */
  private createGoalZone(): void {
    const goal = this.levelConfig.goal;
    
    this.goalZone = new Phaser.Geom.Rectangle(
      goal.position.x - goal.size.width / 2,
      GAME_HEIGHT - goal.position.y - goal.size.height / 2,
      goal.size.width,
      goal.size.height
    );

    // 绘制目标区域
    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(COLOR_GOAL, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, COLOR_GOAL);
    goalGraphics.strokeRectShape(this.goalZone);

    // 添加目标标签
    this.add.text(this.goalZone.centerX, this.goalZone.y - 10, '终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建材质选择按钮
   */
  private createMaterialButtons(): void {
    const buttonY = 20;
    const buttonWidth = 100;
    const buttonHeight = 30;
    const startX = 10;

    const materials = ['ice', 'wood', 'sandpaper'];
    
    materials.forEach((material, index) => {
      const config = this.frictionLevels[material as keyof FrictionLevelsConfig];
      if (!config) return;

      const buttonX = startX + index * (buttonWidth + 10);
      
      const container = this.createMaterialButton(buttonX, buttonY, buttonWidth, buttonHeight, config, material);
      this.materialButtons.set(material, container);
    });

    // 默认选中木地板
    this.selectMaterial('wood');
  }

  /**
   * 创建单个材质按钮
   */
  private createMaterialButton(
    x: number,
    y: number,
    width: number,
    height: number,
    config: { coefficient: number; name: string; color: number; description?: string },
    material: string
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(config.color, 0.8);
    background.fillRoundedRect(0, 0, width, height, 5);
    background.lineStyle(2, 0xffffff, 0.5);
    background.strokeRoundedRect(0, 0, width, height, 5);

    const label = this.add.text(width / 2, height / 2, `${config.name}`, {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    container.add([background, label]);
    container.setSize(width, height);
    container.setInteractive();

    container.on('pointerover', () => {
      background.clear();
      background.fillStyle(config.color, 1);
      background.fillRoundedRect(0, 0, width, height, 5);
      background.lineStyle(2, 0xffffff, 1);
      background.strokeRoundedRect(0, 0, width, height, 5);
    });

    container.on('pointerout', () => {
      const isSelected = this.selectedMaterial === material;
      background.clear();
      background.fillStyle(config.color, isSelected ? 1 : 0.8);
      background.fillRoundedRect(0, 0, width, height, 5);
      background.lineStyle(2, isSelected ? 0xffffff : 0xaaaaaa, isSelected ? 2 : 1);
      background.strokeRoundedRect(0, 0, width, height, 5);
    });

    container.on('pointerdown', () => {
      this.selectMaterial(material);
    });

    return container;
  }

  /**
   * 选择材质并应用
   */
  private selectMaterial(material: string): void {
    this.selectedMaterial = material;
    
    // 更新按钮样式
    this.materialButtons.forEach((button, key) => {
      const isSelected = key === material;
      const config = this.frictionLevels[key as keyof FrictionLevelsConfig];
      if (!config) return;

      const graphics = button.getAt(0) as Phaser.GameObjects.Graphics;
      graphics.clear();
      graphics.fillStyle(config.color, isSelected ? 1 : 0.8);
      graphics.fillRoundedRect(0, 0, 100, 30, 5);
      graphics.lineStyle(2, isSelected ? 0xffffff : 0xaaaaaa, isSelected ? 2 : 1);
      graphics.strokeRoundedRect(0, 0, 100, 30, 5);
    });

    // 应用选中的材质到所有平台
    this.applyMaterialToPlatforms(material);
  }

  /**
   * 将选中材质应用到所有平台
   */
  private applyMaterialToPlatforms(material: string): void {
    const config = this.frictionLevels[material as keyof FrictionLevelsConfig];
    if (!config) return;

    // 更新物理世界的平台摩擦系数
    for (const platform of this.levelConfig.platforms) {
      const body = this.physicsWorld.getBody(platform.id);
      if (body) {
        const fixture = body.getFixtureList();
        if (fixture) {
          fixture.setFriction(config.coefficient);
        }
      }
    }

    // 显示当前材质提示
    this.updateMaterialInfo(config);
  }

  /**
   * 更新材质信息显示
   */
  private updateMaterialInfo(config: { coefficient: number; name: string; description?: string }): void {
    const infoText = this.children.getByName('materialInfo') as Phaser.GameObjects.Text;
    if (infoText) {
      infoText.setText(`当前材质: ${config.name} (μ=${config.coefficient})`);
    }
  }

  /**
   * 创建教程 UI
   */
  private createTutorialUI(): void {
    const tutorial = this.levelConfig.tutorial;

    // 公式展示
    this.add.text(GAME_WIDTH / 2, 70, tutorial?.formula ?? 'f = μ · N', {
      fontSize: '24px',
      color: '#ffd700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式解释
    if (tutorial?.explanation) {
      this.add.text(GAME_WIDTH / 2, 100, tutorial.explanation, {
        fontSize: '12px',
        color: '#cccccc',
        fontFamily: 'Arial',
        wordWrap: { width: GAME_WIDTH - 40 },
      }).setOrigin(0.5);
    }

    // 材质信息
    const woodConfig = this.frictionLevels.wood;
    const infoText = this.add.text(10, 60, `当前材质: ${woodConfig?.name ?? '木地板'} (μ=${woodConfig?.coefficient ?? 0.4})`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });
    infoText.setName('materialInfo');
  }

  /**
   * 创建状态 UI
   */
  private createStatusUI(): void {
    this.add.text(10, GAME_HEIGHT - 20, '拖拽火柴人头部，观察不同材质上的滑动效果', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 检查胜利条件
   */
  private checkVictory(): void {
    const headPos = this.stickman.getHeadPosition();
    
    if (this.goalZone.contains(headPos.x, headPos.y) && !this.isVictory) {
      this.isVictory = true;
      this.showVictoryMessage();
    }
  }

  /**
   * 显示胜利消息
   */
  private showVictoryMessage(): void {
    const gameState = getGameState();
    gameState.completeLevel(4, this.elapsedTime);

    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.7);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, '🎉 挑战成功！', {
      fontSize: '32px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 10,
      `你成功掌握了摩擦力公式 f=μN\n使用材质: ${this.selectedMaterial}`,
      {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    this.createLevel4CompletionButtons(victoryBg);
  }

  private createLevel4CompletionButtons(overlay: Phaser.GameObjects.Graphics): void {
    const gameState = getGameState();

    if (gameState.isLevelUnlocked(5)) {
      const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 60, '下一关', {
        fontSize: '18px',
        color: '#4ade80',
        fontFamily: 'Arial',
        backgroundColor: '#333333',
        padding: { x: 15, y: 8 },
      }).setOrigin(0.5).setInteractive();

      nextBtn.on('pointerdown', () => {
        this.scene.start('Level5Scene');
      });
    }

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 60, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
      }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 100, '重试', {
      fontSize: '16px',
      color: '#ffd700',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      overlay.destroy();
      this.restartLevel();
    });
  }

  /**
   * 重启关卡
   */
  private restartLevel(): void {
    this.isVictory = false;
    this.scene.restart();
  }

  /**
   * 获取默认关卡配置
   */
  private getDefaultConfig(): Level4Config {
    return {
      id: 'level-4',
      name: '摩擦力探索',
      description: '探索摩擦力公式 f=μN',
      version: '1.0.0',
      physics: { gravity: { x: 0, y: -10 } },
      player: { startPosition: { x: 100, y: 100 } },
      platforms: [
        { id: 'ice-platform', position: { x: 150, y: 20 }, size: { width: 200, height: 20 }, friction: FRICTION_ICE, color: COLOR_ICE },
        { id: 'wood-platform', position: { x: 400, y: 20 }, size: { width: 200, height: 20 }, friction: FRICTION_WOOD, color: COLOR_WOOD },
        { id: 'sandpaper-platform', position: { x: 650, y: 20 }, size: { width: 150, height: 20 }, friction: FRICTION_SANDPAPER, color: COLOR_SANDPAPER },
      ],
      goal: { position: { x: 700, y: 80 }, size: { width: 80, height: 60 } },
      frictionLevels: DEFAULT_FRICTION_LEVELS,
      tutorial: {
        formula: 'f = μ · N',
        explanation: '摩擦力 = 摩擦系数 × 正压力',
        hint: '尝试在不同材质上推动火柴人',
      },
    };
  }

  /**
   * 获取选中的材质
   */
  getSelectedMaterial(): string {
    return this.selectedMaterial;
  }

  /**
   * 获取摩擦系数
   */
  getFrictionCoefficient(): number {
    const config = this.frictionLevels[this.selectedMaterial as keyof FrictionLevelsConfig];
    return config?.coefficient ?? FRICTION_WOOD;
  }

  /**
   * 检查是否胜利
   */
  getIsVictory(): boolean {
    return this.isVictory;
  }

  /**
   * 获取物理世界
   */
  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  /**
   * 获取火柴人
   */
  getStickman(): Stickman {
    return this.stickman;
  }
}